import * as React from 'react';
import Modal from '@/components/atoms/Modal';
import { FileInput } from '@/components/atoms/FileInput';
import Loader from '@/components/atoms/Loader/Loader';
import { Button } from '@/components/atoms/Button';
import { computeText } from '@/locales/utils';
import { SearchBar } from '@/components/atoms/SearchBar';
import { SvyIcon } from '@/components/savory/SvyIcon';
import { Checkbox } from '@/components/atoms/Checkbox';
import { Text } from '@/components/atoms/Text';
import styled from '@emotion/styled';
import { ImageInputProps, Sections } from './ImageInput.interface';
import { useImageInputVM, SECTION_CONFIG } from './ImageInput.viewmodel';
import {
    ImageContainer,
    ImageIconButton,
    ImageIconButtonContainer,
    ImageInputModalContainer,
    LoaderContainer,
    TabsContainer,
} from './ImageInput.style';
import { LibraryGrid } from './components/LibraryGrid';

const ResizeImage = styled.div`
    margin-top: 20px;
    align-self: flex-start;
    text-align: left;
`;

const UploadErrorText = styled.div`
    color: #dc3545;
    font-size: 14px;
    margin-top: 8px;
    text-align: center;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ImageInput = React.forwardRef<HTMLInputElement, ImageInputProps>((props, _ref) => {
    const { value, onChange, isDisabled, className, ...rest } = props;
    const {
        currentImage,
        currentSection,
        disableResize,
        handleResizeImageChanged,
        handleCloseModal,
        handleImageClick,
        handleOpenModal,
        handleSetSearch,
        handleSetSection,
        handleUploadImage,
        intl,
        isLibraryLoading,
        isModalOpen,
        isUploadLoading,
        libraryFiles,
        resize,
        rowVirtualizer,
        search,
        uploadError,
        virtualListRef,
    } = useImageInputVM(props);

    return (
        <div className={className}>
            <ImageContainer>
                {currentImage ? (
                    <img src={currentImage.fullUrl} alt="input" {...rest} />
                ) : (
                    <span className="ri-image-line" />
                )}
                {!isDisabled && (
                    <ImageIconButtonContainer>
                        {!!currentImage && (
                            <ImageIconButton
                                type="button"
                                onClick={() => onChange?.(null)}
                                destructive={true}
                                data-testid="imageInput-open"
                            >
                                <SvyIcon icon="delete-bin-line" />
                            </ImageIconButton>
                        )}
                        <ImageIconButton type="button" onClick={handleOpenModal} data-testid="imageInput-open">
                            <SvyIcon icon="pencil-line" />
                        </ImageIconButton>
                    </ImageIconButtonContainer>
                )}
            </ImageContainer>
            <Modal
                height={600}
                isOpen={isModalOpen}
                onHide={handleCloseModal}
                title={computeText(intl, 'imageInput.section.modal.title')}
            >
                {/* TODO: use abstracted component like a Tabs */}
                <TabsContainer>
                    {Object.values(Sections).map((section) => (
                        <Button
                            buttonType={currentSection === section ? 'primary' : 'secondary'}
                            onClick={() => handleSetSection(section as Sections)}
                            key={section}
                        >
                            {computeText(intl, SECTION_CONFIG[section as Sections])}
                        </Button>
                    ))}
                </TabsContainer>
                <ImageInputModalContainer data-testid="imageInput-modalContainer">
                    {currentSection === Sections.Library && (
                        <>
                            <SearchBar value={search} onChange={handleSetSearch} />
                            <br />
                            <LibraryGrid
                                value={value}
                                handleImageClick={handleImageClick}
                                virtualListRef={virtualListRef}
                                rowVirtualizer={rowVirtualizer}
                                libraryFiles={libraryFiles}
                                isLoading={isLibraryLoading}
                                customLoader={
                                    <LoaderContainer>
                                        <Loader />
                                    </LoaderContainer>
                                }
                            />
                        </>
                    )}
                    {currentSection === Sections.Upload && (
                        <>
                            {isUploadLoading ? (
                                <Loader />
                            ) : (
                                <React.Suspense>
                                    <FileInput
                                        mode="full"
                                        onChange={handleUploadImage}
                                        accept={{
                                            'image/*': ['.jpeg', '.png'],
                                        }}
                                    />
                                    {uploadError && <UploadErrorText>{computeText(intl, uploadError)}</UploadErrorText>}
                                    {!disableResize && (
                                        <ResizeImage>
                                            <Checkbox
                                                value={resize}
                                                label={computeText(intl, 'imageInput.section.modal.resize.label')}
                                                onChange={handleResizeImageChanged}
                                            />
                                            <Text
                                                text={computeText(intl, 'imageInput.section.modal.resize.details')}
                                            ></Text>
                                        </ResizeImage>
                                    )}
                                </React.Suspense>
                            )}
                        </>
                    )}
                </ImageInputModalContainer>
            </Modal>
        </div>
    );
});
ImageInput.displayName = 'ImageInput';
